import { render, Component } from 'preact'
import assert from 'assert'
import App, { state, props } from 'cerebral'
import { Container, connect } from '.'

const Empty = () => null

describe('Preact', () => {
  let container, $, mount

  before(() => {
    container = document.createElement('div')
    document.body.appendChild(container)
    $ = (s) => container.querySelector(s)
    mount = (jsx) => {
      return render(jsx, container, container.firstChild)
    }
  })
  afterEach(() => {
    mount(<Empty />)
    container.innerHTML = ''
  })
  after(() => {
    document.body.removeChild(container)
    container = null
  })
  describe('container', () => {
    it('should be able to expose app', () => {
      const app = App({
        state: {
          foo: 'bar',
        },
      })
      const TestComponent = connect(
        {
          foo: state`foo`,
        },
        (props) => {
          return <div>{props.foo}</div>
        }
      )
      mount(
        <Container app={app}>
          <TestComponent />
        </Container>
      )

      assert.strictEqual($('div').innerHTML, 'bar')
    })
    it('should throw when no app provided', () => {
      const TestComponent = connect(
        {
          foo: state`foo`,
        },
        (props) => {
          return <div>{props.foo}</div>
        }
      )
      assert.throws(
        () => {
          mount(
            <Container>
              <TestComponent />
            </Container>
          )
        },
        (err) => {
          if (err instanceof Error) {
            return (
              err.message ===
              'Cerebral - You are not passing a Cerebral app to Container'
            )
          }
        }
      )
    })
    it('should throw when container component is not provided', () => {
      const TestComponent = connect(
        {
          foo: state`foo`,
        },
        (props) => {
          return <div>{props.foo}</div>
        }
      )
      assert.throws(
        () => {
          mount(<TestComponent />)
        },
        (err) => {
          if (err instanceof Error) {
            return (
              err.message ===
              'Cerebral - Can not find controller, did you remember to use the Container component? Read more at: http://cerebraljs.com/docs/api/components.html#preact-(beta)'
            )
          }
        }
      )
    })
    it('should be able to unregister component from container after unmounting component', () => {
      const app = App(
        {
          state: {
            foo: 'bar',
          },
        },
        {
          devtools: {
            init() {},
            send() {},
            updateWatchMap() {},
            updateComputedMap() {},
            sendWatchMap() {},
          },
        }
      )
      const TestComponent = connect(
        {
          foo: state`foo`,
        },
        (props) => {
          return <div>{props.foo}</div>
        }
      )
      mount(
        <Container app={app}>
          <TestComponent />
        </Container>
      )
      assert.strictEqual($('div').innerHTML, 'bar')
      mount(<Empty />)
      container.innerHTML = ''
      assert.deepStrictEqual(app.dependencyStore.getAllUniqueEntities(), [])
    })
  })
  describe('connect', () => {
    it('should convert component to json', () => {
      const app = App({
        state: {
          foo: 'bar',
        },
      })
      const MyComponent = (props) => {
        return <div>{props.foo}</div>
      }
      MyComponent.displayName = 'Test'
      const TestComponent = connect(
        {
          foo: state`foo`,
        },
        MyComponent
      )

      // Access static toJSON method instead of using a ref
      assert.strictEqual(TestComponent.toJSON(), 'Test')

      // Render the component
      mount(
        <Container app={app}>
          <TestComponent />
        </Container>
      )

      assert.strictEqual($('div').innerHTML, 'bar')
    })

    it('should render ones where multiple state matches', (done) => {
      const app = App({
        state: {
          foo: 'bar',
          bar: 'foo',
        },
        sequences: {
          test: [
            ({ store }) => {
              store.set('foo', 'bar2')
              store.set('bar', 'foo2')
            },
          ],
        },
      })
      let renderCount = 0
      const TestComponent = connect(
        {
          foo: state`foo`,
          bar: state`bar`,
        },
        (props) => {
          renderCount++
          return <div>{props.foo}</div>
        }
      )
      mount(
        <Container app={app}>
          <TestComponent />
        </Container>
      )

      assert.strictEqual($('div').innerHTML, 'bar')
      assert.strictEqual(renderCount, 1)

      // Force a synchronous update when running the test
      app.getSequence('test')()

      // Needed for Preact X - ensure the update propagates
      setTimeout(() => {
        assert.strictEqual(renderCount, 2)
        assert.strictEqual($('div').innerHTML, 'bar2')
        done()
      }, 0)
    })

    it('should rerender affected components', (done) => {
      let renderCount = 0
      const app = App({
        state: {
          foo: 'bar',
        },
        sequences: {
          methodCalled: [({ store }) => store.set('foo', 'bar2')],
        },
      })
      class TestComponentClass2 extends Component {
        render() {
          renderCount++
          return <div />
        }
      }
      const TestComponent2 = connect({}, TestComponentClass2)
      class TestComponentClass extends Component {
        render() {
          renderCount++
          return (
            <div>
              <TestComponent2 />
            </div>
          )
        }
      }
      const TestComponent = connect(
        {
          foo: state`foo`,
        },
        TestComponentClass
      )
      mount(
        <Container app={app}>
          <TestComponent />
        </Container>
      )

      // Initial render count is 2 (parent + child)
      assert.strictEqual(renderCount, 2)

      app.getSequence('methodCalled')()

      // After update it should be 3 (parent renders once more)
      // Add a timeout to ensure proper handling of async updates
      setTimeout(() => {
        assert.strictEqual(renderCount, 3)
        done()
      }, 0)
    })

    it('should allow props tag and computed', () => {
      const app = App({
        state: {
          foo: {
            baz: 'mip',
          },
          aComputed: (get) => get(props`foo`),
        },
      })
      const TestComponent = connect(
        {
          a: state`aComputed`,
          b: props`bar.id`,
          c: state`foo.${props`propKey`}`,
        },
        ({ a, b, c }) => {
          return <div>{a + b + c}</div>
        }
      )
      mount(
        <Container app={app}>
          <TestComponent foo="bar" bar={{ id: '1' }} propKey="baz" />
        </Container>
      )
      assert.strictEqual($('div').innerHTML, 'bar1mip')
    })
    it('should update on props change', (done) => {
      const app = App({})

      // Track component instance
      let componentInstance = null

      // Create simple components
      class TestComponentClass2 extends Component {
        render() {
          return <div data-testid="content-div">{this.props.foo}</div>
        }
      }

      const TestComponent2 = connect({}, TestComponentClass2)

      class TestComponentClass extends Component {
        constructor(props) {
          super(props)
          this.state = { foo: 'bar' }
          componentInstance = this
        }

        changePath() {
          this.setState({ foo: 'bar2' }, () => {
            assert.strictEqual(
              document.querySelector('[data-testid="content-div"]').innerHTML,
              'bar2'
            )
            done()
          })
        }

        render() {
          return (
            <div>
              <TestComponent2 foo={this.state.foo} />
            </div>
          )
        }
      }

      const TestComponent = connect({}, TestComponentClass)

      // Mount component
      mount(
        <Container app={app}>
          <TestComponent />
        </Container>
      )

      // Verify initial state
      assert.strictEqual(
        document.querySelector('[data-testid="content-div"]').innerHTML,
        'bar'
      )

      // Verify state update
      setTimeout(() => {
        if (componentInstance) {
          componentInstance.changePath()
        } else {
          done(new Error("Couldn't access component instance"))
        }
      }, 0)
    })
  })
})
